import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, InputBase, Paper, Stack, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TerminalIcon from '@mui/icons-material/Terminal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../../api/socket';
import { triggerClaude, wpNext, wpNextNumber, wpClaim, wpComplete, wpRelease } from '../../api/claude';

interface TermLine { ts: string; text: string; isInput?: boolean; isPermError?: boolean }

interface TokenSummary { input: number; output: number; model: string }

interface Props {
  activeWpId: string | null;
  onWpClaimed: (wpId: string) => void;
  onWpReleased: () => void;
}

export function ClaudeCommandTerminal({ activeWpId, onWpClaimed, onWpReleased }: Props) {
  const qc = useQueryClient();
  const [lines, setLines] = useState<TermLine[]>([]);
  const [running, setRunning] = useState(false);
  const [claimInput, setClaimInput] = useState('');
  const [inputText, setInputText] = useState('');
  const [tokenSummary, setTokenSummary] = useState<TokenSummary | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const jobIdRef = useRef<string | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  function print(text: string, isPermError = false) {
    setLines((prev) => [...prev, { ts: new Date().toISOString(), text, isPermError }]);
  }

  function printJson(obj: unknown) {
    try { print(JSON.stringify(obj, null, 2)); } catch { print(String(obj)); }
  }

  function handleSendInput() {
    const text = inputText.trim();
    if (!text || !jobIdRef.current) return;
    setInputText('');
    setLines((prev) => [...prev, { ts: new Date().toISOString(), text: `← ${text}`, isInput: true }]);
    getSocket().emit('claude:input', { job_id: jobIdRef.current, text });
  }

  async function handleWpNext() {
    if (running) return;
    print('> wp next');
    try {
      const wp = await wpNext();
      printJson(wp);
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
    }
  }

  async function handleWpNextNumber() {
    if (running) return;
    print('> wp next number');
    try {
      const result = await wpNextNumber();
      printJson(result);
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
    }
  }

  async function handleClaim(wpId: string) {
    print(`> wp claim ${wpId}`);
    try {
      const result = await wpClaim(wpId);
      printJson(result);
      onWpClaimed(wpId);
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
    }
  }

  async function handleClaimInput() {
    const raw = claimInput.trim();
    if (!raw) return;
    setClaimInput('');

    if (raw.toLowerCase() === 'next') {
      print('> wp next');
      let wpId: string;
      try {
        const wp = await wpNext();
        printJson(wp);
        wpId = typeof wp === 'object' && wp !== null && 'wp_number' in wp
          ? `WP${(wp as { wp_number: number }).wp_number}`
          : String(wp);
      } catch (e) {
        print(`Error: ${(e as Error).message}`);
        return;
      }
      await handleClaim(wpId);
    } else {
      const upper = raw.toUpperCase();
      const wpId = upper.startsWith('WP') ? upper : `WP${upper}`;
      await handleClaim(wpId);
    }
  }

  async function handleComplete() {
    if (!activeWpId) { print('No active WP — claim one first.'); return; }
    print(`> wp complete ${activeWpId}`);
    try {
      const result = await wpComplete(activeWpId);
      printJson(result);
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
    }
  }

  async function handleRelease() {
    if (!activeWpId) { print('No active WP — claim one first.'); return; }
    print(`> wp release ${activeWpId}`);
    try {
      const result = await wpRelease(activeWpId);
      printJson(result);
      onWpReleased();
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
    }
  }

  async function handleTrigger(step: 'claude' | 'git') {
    if (!activeWpId) { print('No active WP — claim one first.'); return; }
    if (running) return;
    setRunning(true);
    setTokenSummary(null);
    print(`> trigger ${step} ${activeWpId}`);

    let jobId: string;
    try {
      const result = await triggerClaude(step, activeWpId);
      jobId = result.job_id;
      jobIdRef.current = jobId;
      print(`Job started: ${jobId}`);
    } catch (e) {
      print(`Error: ${(e as Error).message}`);
      setRunning(false);
      return;
    }

    const socket = getSocket();
    socket.emit('claude:trigger:subscribe', { job_id: jobId });

    const onOutput = (data: { job_id: string; line: string; ts: string }) => {
      if (data.job_id !== jobId) return;
      const { line, ts } = data;

      if (line.startsWith('PERMISSION_ERROR: ')) {
        setLines((prev) => [...prev, { ts, text: line, isPermError: true }]);
        return;
      }

      if (line.startsWith('TOKENS: ')) {
        const m = line.match(/input=(\d+)\s+output=(\d+)\s+model=(\S+)/);
        if (m) setTokenSummary({ input: parseInt(m[1]), output: parseInt(m[2]), model: m[3] });
      }

      setLines((prev) => [...prev, { ts, text: line }]);
    };

    const onDone = (data: { job_id: string; exit_code: number }) => {
      if (data.job_id !== jobId) return;
      print(`--- done (exit ${data.exit_code}) ---`);
      jobIdRef.current = null;
      setRunning(false);
      void qc.invalidateQueries({ queryKey: ['claude', 'usage'] });
      socket.off('claude:output', onOutput);
      socket.off('claude:done', onDone);
      socket.emit('claude:trigger:unsubscribe', { job_id: jobId });
    };

    socket.on('claude:output', onOutput);
    socket.on('claude:done', onDone);
  }

  return (
    <>
      <Paper
        sx={{
          bgcolor: '#0d1117',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Title bar */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1, bgcolor: '#161b22', borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <TerminalIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e6edf3', flex: 1 }}>
            Claude Commands
          </Typography>
          {activeWpId && (
            <Chip label={activeWpId} size="small" color="primary" variant="outlined" />
          )}
          {running && <CircularProgress size={14} />}
        </Stack>

        {/* Output */}
        <Box
          sx={{
            height: 300,
            overflowY: 'auto',
            px: 2,
            py: 1,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            fontSize: '0.75rem',
            color: '#e6edf3',
          }}
        >
          {lines.length === 0 && (
            <Typography variant="caption" sx={{ color: '#8b949e' }}>
              Select a command below to get started.
            </Typography>
          )}
          {lines.map((l, i) => (
            <Box
              key={i}
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                color: l.isPermError ? 'warning.light' : l.isInput ? '#4dd0c4' : undefined,
              }}
            >
              <Typography component="span" sx={{ color: '#8b949e', fontSize: '0.65rem', mr: 1 }}>
                {new Date(l.ts).toLocaleTimeString()}
              </Typography>
              {l.text}
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>

        {/* Token summary — shown after job completes */}
        {tokenSummary && !running && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 0.75, bgcolor: '#161b22', borderTop: '1px solid', borderColor: 'divider' }}
          >
            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography variant="caption" sx={{ color: 'success.light', fontFamily: 'monospace' }}>Done</Typography>
            <Typography variant="caption" sx={{ color: '#484f58' }}>·</Typography>
            <Typography variant="caption" sx={{ color: '#e6edf3', fontFamily: 'monospace' }}>
              ↑ {tokenSummary.input.toLocaleString()} in
            </Typography>
            <Typography variant="caption" sx={{ color: '#484f58' }}>·</Typography>
            <Typography variant="caption" sx={{ color: '#e6edf3', fontFamily: 'monospace' }}>
              ↓ {tokenSummary.output.toLocaleString()} out
            </Typography>
            <Typography variant="caption" sx={{ color: '#484f58' }}>·</Typography>
            <Typography variant="caption" sx={{ color: '#8b949e', fontFamily: 'monospace' }}>
              {tokenSummary.model}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              sx={{ fontSize: '0.65rem', py: 0, minWidth: 0, color: '#8b949e' }}
              onClick={() => setTokenSummary(null)}
            >
              dismiss
            </Button>
          </Stack>
        )}

        {/* Chat input — visible while a job is running */}
        {running && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ px: 1.5, py: 0.75, bgcolor: '#0d1117', borderTop: '1px solid', borderColor: 'divider' }}
          >
            <InputBase
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendInput(); } }}
              placeholder="Reply to Claude…"
              autoFocus
              fullWidth
              sx={{
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                fontSize: '0.8rem',
                color: '#e6edf3',
                '& input::placeholder': { color: '#8b949e', opacity: 1 },
              }}
            />
            <IconButton
              size="small"
              onClick={handleSendInput}
              disabled={!inputText.trim()}
              sx={{ color: 'primary.main', ml: 0.5 }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}

        {/* Commands */}
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={1}
          sx={{ px: 2, py: 1.5, bgcolor: '#161b22', borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Chip label="wp next" size="small" onClick={handleWpNext} disabled={running} clickable />
          <Chip label="wp next number" size="small" onClick={handleWpNextNumber} disabled={running} clickable />
          <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); void handleClaimInput(); }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <InputBase
              value={claimInput}
              onChange={(e) => setClaimInput(e.target.value)}
              placeholder="WP97 or next"
              disabled={running}
              sx={{
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                fontSize: '0.72rem',
                color: '#e6edf3',
                bgcolor: '#0d1117',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.25,
                width: 110,
                '& input::placeholder': { color: '#8b949e', opacity: 1 },
              }}
            />
            <Button
              type="submit"
              size="small"
              variant="outlined"
              disabled={running || !claimInput.trim()}
              sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0, textTransform: 'none' }}
            >
              claim
            </Button>
          </Box>
          <Chip label="wp complete" size="small" onClick={handleComplete} disabled={running || !activeWpId} clickable />
          <Chip label="wp release" size="small" onClick={() => void handleRelease()} disabled={running || !activeWpId} clickable />
          <Chip
            label="trigger claude-code"
            size="small"
            color="primary"
            onClick={() => void handleTrigger('claude')}
            disabled={running || !activeWpId}
            clickable
          />
          <Chip
            label="git commit & push"
            size="small"
            color="secondary"
            onClick={() => void handleTrigger('git')}
            disabled={running || !activeWpId}
            clickable
          />
        </Stack>
      </Paper>

    </>
  );
}
